import React, { useState, useEffect, useMemo } from 'react';
import { gql, useQuery, useMutation } from '@apollo/client';
import SingleColumnTemplate from '../../../../ui/layouts/SingleColumnTemplate.jsx';
import Card from '../../../../ui/canon/Card.jsx';
import Button from '../../../../ui/canon/Button.jsx';
import Input from '../../../../ui/canon/Input.jsx';
import { ArrowLeftIcon, EyeIcon, CheckCircleIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useApolloClient } from '@apollo/client';
import client from '../../lib/apolloClient';

const CURRENT_USER_QUERY = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
    }
  }
`;

const COACH_PROFILE_QUERY = gql`
  query CoachProfile($user_id: ID) {
    coachProfile(user_id: $user_id) {
      id
      name
      headline
      bio
      avatar_url
      credentials
      specializations
      org_name
      timezone
      rating
      review_count
      program_count
      verified
    }
  }
`;

const UPDATE_COACH_PROFILE = gql`
  mutation UpdateCoachProfile(
    $name: String
    $headline: String
    $bio: String
    $org_name: String
    $timezone: String
    $credentials: [String!]
    $specializations: [String!]
    $avatar_url: String
  ) {
    update_coach_profile(
      name: $name
      headline: $headline
      bio: $bio
      org_name: $org_name
      timezone: $timezone
      credentials: $credentials
      specializations: $specializations
      avatar_url: $avatar_url
    ) {
      id
      name
      headline
      bio
      avatar_url
      credentials
      specializations
      org_name
      timezone
      rating
      review_count
      program_count
      verified
    }
  }
`;

const CREATE_DOCUMENT = gql`
  mutation CreateDocument($file: File!, $name: String, $category: String, $document_type: Int) {
    createDocument(file: $file, name: $name, category: $category, document_type: $document_type) {
      id
      download_url
    }
  }
`;

export default function CoachProfileSettingsContainer() {
  const apolloClient = useApolloClient();
  const { data: userData } = useQuery(CURRENT_USER_QUERY);
  const currentUserId = userData?.getCurrentUser?.id;

  const { data: profileData, loading, error, refetch } = useQuery(COACH_PROFILE_QUERY, {
    variables: { user_id: currentUserId },
    skip: !currentUserId,
  });

  const coachProfile = profileData?.coachProfile;

  const [formData, setFormData] = useState({
    name: '',
    headline: '',
    bio: '',
    org_name: '',
    timezone: 'America/New_York',
    credentials: [],
    specializations: [],
  });

  const [newCredential, setNewCredential] = useState('');
  const [newSpecialization, setNewSpecialization] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
    const [avatarFile, setAvatarFile] = useState(null);
    const [avatarPreview, setAvatarPreview] = useState(null);
    const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
    const [uploadError, setUploadError] = useState(null);

    const [updateProfile] = useMutation(UPDATE_COACH_PROFILE);

  // Initialize form data when profile loads
  useEffect(() => {
    if (coachProfile) {
      setFormData({
        name: coachProfile.name || '',
        headline: coachProfile.headline || '',
        bio: coachProfile.bio || '',
        org_name: coachProfile.org_name || '',
        timezone: coachProfile.timezone || 'America/New_York',
        credentials: coachProfile.credentials || [],
        specializations: coachProfile.specializations || [],
      });
      setAvatarPreview(coachProfile.avatar_url || null);
    }
  }, [coachProfile]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addCredential = () => {
    if (newCredential.trim() && !formData.credentials.includes(newCredential.trim())) {
      setFormData((prev) => ({
        ...prev,
        credentials: [...prev.credentials, newCredential.trim()],
      }));
      setNewCredential('');
    }
  };

  const removeCredential = (credential) => {
    setFormData((prev) => ({
      ...prev,
      credentials: prev.credentials.filter((c) => c !== credential),
    }));
  };

  const addSpecialization = () => {
    if (newSpecialization.trim() && !formData.specializations.includes(newSpecialization.trim())) {
      setFormData((prev) => ({
        ...prev,
        specializations: [...prev.specializations, newSpecialization.trim()],
      }));
      setNewSpecialization('');
    }
  };

  const removeSpecialization = (specialization) => {
    setFormData((prev) => ({
      ...prev,
      specializations: prev.specializations.filter((s) => s !== specialization),
    }));
  };

  const handleAvatarSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setUploadError('Invalid file type. Only JPEG, PNG, GIF, and WebP images are allowed.');
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError(`File size exceeds 5MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB)`);
      return;
    }

    setUploadError(null);
    setAvatarFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setAvatarPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setUploadError(null);

      // Upload avatar first if a new file was selected
      // Use the same pattern as TaskDetailModal - create a Document via Shrine
      // Note: Use client.mutate directly (not useMutation hook) for file uploads
      let avatarUrl = coachProfile?.avatar_url;
      if (avatarFile) {
        setIsUploadingAvatar(true);
        try {
          const docResult = await client.mutate({
            mutation: CREATE_DOCUMENT,
            variables: {
              file: [avatarFile],
              name: `coach_avatar_${coachProfile?.id || 'new'}_${Date.now()}`,
              category: 'other',
              document_type: 0, // default_document
            },
            context: { hasUpload: true },
          });

          if (!docResult.data || !docResult.data.createDocument) {
            throw new Error('Failed to create document');
          }

          avatarUrl = docResult.data.createDocument.download_url;
          setIsUploadingAvatar(false);
        } catch (uploadErr) {
          setIsUploadingAvatar(false);
          setUploadError(uploadErr.message || 'Failed to upload avatar');
          setIsSaving(false);
          return;
        }
      }

      // Update profile with all fields
      await updateProfile({
        variables: {
          name: formData.name,
          headline: formData.headline,
          bio: formData.bio,
          org_name: formData.org_name,
          timezone: formData.timezone,
          credentials: formData.credentials.filter(Boolean), // Filter out any null/empty values
          specializations: formData.specializations.filter(Boolean), // Filter out any null/empty values
          avatar_url: avatarUrl,
        },
      });

      // Refetch to get updated data
      await refetch();

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      setAvatarFile(null);
    } catch (err) {
      setUploadError(err.message || 'Failed to save changes');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <SingleColumnTemplate title="Profile Settings">
        <div className="text-center py-12">Loading...</div>
      </SingleColumnTemplate>
    );
  }

  if (error || !coachProfile) {
    return (
      <SingleColumnTemplate title="Profile Settings">
        <div className="text-center py-12 text-red-600">
          {error?.message || 'Coach profile not found'}
        </div>
      </SingleColumnTemplate>
    );
  }

  const timezones = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Phoenix', label: 'Arizona (MST)' },
    { value: 'America/Anchorage', label: 'Alaska (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii (HST)' },
  ];

  return (
    <SingleColumnTemplate
      title="Profile Settings"
      subtitle="Manage your public coach profile and credentials"
      actions={
        <div className="flex items-center gap-2">
          <a href={`/coaches/${coachProfile.id}`}>
            <Button variant="outline" size="sm">
              <EyeIcon className="w-4 h-4 mr-2" />
              Preview Public Profile
            </Button>
          </a>
          <Button onClick={handleSave} disabled={isSaving || isUploadingAvatar} size="sm">
            {isSaving || isUploadingAvatar ? (
              'Saving...'
            ) : saveSuccess ? (
              <>
                <CheckCircleIcon className="w-4 h-4 mr-2" />
                Saved!
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </div>
      }
    >
      {/* Back link */}
      <div className="mb-4">
        <a
          href="/coach/portal"
          className="inline-flex items-center gap-2 text-sm text-succession-text-secondary hover:text-succession-text-primary"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Coach Portal
        </a>
      </div>

      {/* Avatar Upload Card */}
      <Card variant="outlined" size="lg" className="bg-succession-bg-primary">
        <h2 className="text-xl font-semibold mb-4">Profile Picture</h2>
        <div className="space-y-4">
          {/* Current Avatar Display */}
          <div className="flex flex-col items-center">
            <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-white shadow-lg bg-succession-bg-secondary">
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar preview" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-succession-bg-secondary flex items-center justify-center text-succession-text-secondary">
                  No image
                </div>
              )}
            </div>
            <p className="mt-3 text-sm font-medium text-succession-text-primary">Current Selection</p>
          </div>

          {/* File Input */}
          <div>
            <label className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-succession-border rounded cursor-pointer text-succession-text-primary hover:bg-succession-hover-bg bg-succession-bg-primary">
              Choose Image
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleAvatarSelect}
                disabled={isUploadingAvatar}
              />
            </label>
            <p className="text-xs text-succession-text-secondary mt-2">
              Supported formats: JPG, PNG, GIF, WebP. Max size: 5MB
            </p>
          </div>

          {uploadError && (
            <div className="text-sm text-red-600 bg-red-50 p-3 rounded border border-red-200">
              {uploadError}
            </div>
          )}
        </div>
      </Card>

      {/* Basic Information Card */}
      <Card variant="outlined" size="lg" className="bg-succession-bg-primary">
        <h2 className="text-xl font-semibold mb-6">Basic Information</h2>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm text-succession-text-primary font-medium">Full Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                placeholder="Dr. Sarah Mitchell"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm text-succession-text-primary font-medium">Organization Name</label>
              <Input
                value={formData.org_name}
                onChange={(e) => handleFieldChange('org_name', e.target.value)}
                placeholder="Mitchell Advisory Group"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-succession-text-primary font-medium">Professional Headline *</label>
            <Input
              value={formData.headline}
              onChange={(e) => handleFieldChange('headline', e.target.value)}
              placeholder="CEPA | Exit Planning Strategist"
            />
            <p className="text-xs text-succession-text-secondary">
              A brief tagline that appears under your name (e.g., "CEPA | Exit Planning Strategist")
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-succession-text-primary font-medium">Professional Bio *</label>
            <textarea
              className="w-full border border-succession-border bg-succession-bg-primary text-succession-text-primary rounded-md px-3 py-2 min-h-[120px] resize-none"
              value={formData.bio}
              onChange={(e) => handleFieldChange('bio', e.target.value)}
              placeholder="Tell potential clients about your experience, expertise, and approach..."
            />
            <p className="text-xs text-succession-text-secondary">{formData.bio.length} characters</p>
          </div>

          <div className="space-y-2">
            <label className="text-sm text-succession-text-primary font-medium">Timezone</label>
            <select
              className="w-full border border-succession-border bg-succession-bg-primary text-succession-text-primary rounded-md px-3 py-2"
              value={formData.timezone}
              onChange={(e) => handleFieldChange('timezone', e.target.value)}
            >
              {timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Credentials Card */}
      <Card variant="outlined" size="lg" className="bg-succession-bg-primary">
        <h2 className="text-xl font-semibold mb-4">Credentials & Certifications</h2>
        <p className="text-sm text-succession-text-secondary mb-4">
          Add your professional credentials (e.g., CEPA, CPA, CFP, MBA)
        </p>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {formData.credentials.map((credential) => (
              <span
                key={credential}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-succession-bg-secondary text-succession-text-primary text-sm border border-succession-border"
              >
                {credential}
                <button
                  onClick={() => removeCredential(credential)}
                  className="hover:bg-succession-hover-bg rounded-full p-0.5"
                  type="button"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </span>
            ))}
            {formData.credentials.length === 0 && (
              <p className="text-sm text-succession-text-secondary">No credentials added yet</p>
            )}
          </div>
          <div className="flex gap-2">
            <Input
              value={newCredential}
              onChange={(e) => setNewCredential(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addCredential()}
              placeholder="Add credential (e.g., CEPA)"
              className="flex-1"
            />
            <Button variant="outline" onClick={addCredential}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Add
            </Button>
          </div>
        </div>
      </Card>

      {/* Specializations Card */}
      <Card variant="outlined" size="lg" className="bg-succession-bg-primary">
        <h2 className="text-xl font-semibold mb-4">Areas of Specialization</h2>
        <p className="text-sm text-succession-text-secondary mb-4">What are your key areas of expertise?</p>
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {formData.specializations.map((specialization) => (
              <span
                key={specialization}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-succession-bg-secondary text-succession-text-primary text-sm border border-succession-border"
              >
                {specialization}
                <button
                  onClick={() => removeSpecialization(specialization)}
                  className="hover:bg-succession-hover-bg rounded-full p-0.5"
                  type="button"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </span>
            ))}
            {formData.specializations.length === 0 && (
              <p className="text-sm text-succession-text-secondary">No specializations added yet</p>
            )}
          </div>
          <div className="flex gap-2">
            <Input
              value={newSpecialization}
              onChange={(e) => setNewSpecialization(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addSpecialization()}
              placeholder="Add specialization (e.g., Exit Planning)"
              className="flex-1"
            />
            <Button variant="outline" onClick={addSpecialization}>
              <PlusIcon className="w-4 h-4 mr-2" />
              Add
            </Button>
          </div>
        </div>
      </Card>

      {/* Profile Statistics Card */}
      <Card variant="outlined" size="lg" className="bg-succession-bg-primary">
        <h2 className="text-xl font-semibold mb-4">Profile Statistics</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="text-sm text-succession-text-secondary mb-1">Rating</div>
            <div className="text-2xl font-bold">⭐ {coachProfile.rating || '0.0'}</div>
            <div className="text-xs text-succession-text-secondary">
              {coachProfile.review_count || 0} reviews
            </div>
          </div>
          <div>
            <div className="text-sm text-succession-text-secondary mb-1">Programs</div>
            <div className="text-2xl font-bold">{coachProfile.program_count || 0}</div>
            <div className="text-xs text-succession-text-secondary">published</div>
          </div>
          <div>
            <div className="text-sm text-succession-text-secondary mb-1">Status</div>
            <div className="flex items-center gap-2">
              {coachProfile.verified && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-green-600 text-white text-xs font-medium">
                  <CheckCircleIcon className="w-3 h-3 mr-1" />
                  Verified
                </span>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Bottom Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving || isUploadingAvatar} size="lg">
          {isSaving || isUploadingAvatar ? (
            'Saving...'
          ) : saveSuccess ? (
            <>
              <CheckCircleIcon className="w-4 h-4 mr-2" />
              Saved!
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>
    </SingleColumnTemplate>
  );
}

